import React, {useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {Formik} from 'formik';
import {isEqual} from 'lodash';
import moment from 'moment';
import {useDispatch} from 'react-redux';
import {Dropdown} from 'react-bootstrap';
import {useReceiptsUIContext} from '../ReceiptsUIContext';
import {FilterFromDate, FilterToDate} from '../../../_common/components/FilterDateRange';
import FilterTextField from '../../../_common/components/FilterTextField';
import PopupAddReceipt from '../PopupAddReceipt/components/PopupAddReceipt';
import ButtonExportToExcel from '../../../_common/components/ButtonExportToExcel';
import FiltersBar from '../../../_common/components/FiltersBar';
import ButtonSmartFilter from '../../../_common/components/ButtonSmartFilter';
import {RequestCancellationManager} from '../../../_common/utils/RequestCancellationManager';
import {getReceiptsListIDPrefix} from '../../_redux/receiptsActions';
import * as actions from '../../../Receipts/_redux/receiptsActions';
import {generateExcelFile} from '../../../_common/utils/excel';
import {receiptsHeader} from '../../../_common/constants/exportedFilesMainHeaders';
import {headersReceipts} from '../../../_common/constants/exportedFilesHeaders';
import styles from '../../../Users/pages/users-filter/styles.module.scss';

const prepareFilter = (queryParams, values) => {
  const {agentID, agentName, customerID, invoiceStatusID, fromDate, toDate, receiptID, amount} = values;
  const newQueryParams = {...queryParams};
  const filter = {};
  if (Boolean(agentID)) {
    filter.agentID = agentID;
  } else {
    delete newQueryParams.agentID;
  }
  if (Boolean(agentName)) {
    filter.agentName = agentName;
  } else {
    delete newQueryParams.agentName;
  }
  if (Boolean(customerID)) {
    filter.customerID = customerID;
  } else {
    delete newQueryParams.customerID;
  }
  if (Boolean(invoiceStatusID)) {
    filter.invoiceStatusID = invoiceStatusID;
  } else {
    delete newQueryParams.invoiceStatusID;
  }
  if (Boolean(fromDate)) {
    filter.fromDate = moment(fromDate).format('YYYY-MM-DD');
  } else {
    delete newQueryParams.fromDate;
  }
  if (Boolean(toDate)) {
    filter.toDate = moment(toDate).add(1, 'days').format('YYYY-MM-DD');
  } else {
    delete newQueryParams.toDate;
  }
  if (Boolean(receiptID)) {
    filter.receiptID = receiptID;
  } else {
    delete newQueryParams.receiptID;
  }
  if (Boolean(amount)) {
    filter.amount = amount;
  } else {
    delete newQueryParams.amount;
  }
  return {...newQueryParams, ...filter};
};

const CustomDDItem = React.forwardRef(
  ({children, style, className, 'aria-labelledby': labeledBy}, ref) => (
    <div
      className={`${className} d-flex align-items-center pb-0 mb-0`}
      style={style}
      ref={ref}
    >
      {children}
    </div>
  )
);

export function ReceiptsFilter() {
  const dispatch = useDispatch();
  const receiptsUIContext = useReceiptsUIContext();
  const receiptsUIProps = useMemo(() => {
    return {
      queryParams: receiptsUIContext.queryParams,
      setQueryParams: receiptsUIContext.setQueryParams,
    };
  }, [receiptsUIContext]);
  const [loading, setLoading] = useState(false);

  const applyFilter = (values) => {
    RequestCancellationManager.stopRequests(getReceiptsListIDPrefix);

    const newQueryParams = prepareFilter(receiptsUIProps.queryParams, values);
    if (!isEqual(newQueryParams, receiptsUIProps.queryParams)) {
      receiptsUIProps.setQueryParams(newQueryParams);
    }
  };

  const intl = useIntl();
  const prefix = 'receipts-filter';
  const sfPrefix = 'receipts-smart-filter';

  const qs = new URLSearchParams(window.location.search);
  const agentID = qs.get('agentID') || '';
  const agentName = qs.get('agentName') || '';
  const customerID = qs.get('customerID') || '';
  const invoiceStatusID = qs.get('invoiceStatusID') || '';
  const fromDate = qs.get('fromDate') || '';
  const toDate = Boolean(qs.get('toDate'))
    ? moment(qs.get('toDate')).subtract(1, 'days').toDate()
    : '';
  const receiptID = qs.get('receiptID') || '';
  const amount = qs.get('amount') || '';
  const timePeriodValue = moment(toDate).diff(fromDate, 'days');

  const smartFilterDaysArray = [
    {
      id: 'all',
      timePeriod: '0',
      todayDate: '',
      pastDate: '',
      label: intl.formatMessage({
        id: `${sfPrefix}-all`,
        defaultMessage: 'All',
      })
    },
    {
      id: 'lastDay',
      timePeriod: 1,
      todayDate: moment(),
      pastDate: moment().subtract(1, 'day'),
      label: intl.formatMessage({
        id: `${sfPrefix}-last-day`,
        defaultMessage: 'Last day',
      })
    },
    {
      id: 'lastWeek',
      timePeriod: 7,
      todayDate: moment(),
      pastDate: moment().subtract(1, 'week'),
      label: intl.formatMessage({
        id: `${sfPrefix}-last-week`,
        defaultMessage: 'Last week',
      })
    },
    {
      id: 'last30Days',
      timePeriod: 30,
      todayDate: moment(),
      pastDate: moment().subtract(30, 'days'),
      label: intl.formatMessage({
        id: `${sfPrefix}-30days`,
        defaultMessage: '30 days',
      })
    },
    {
      id: 'last60Days',
      timePeriod: 60,
      todayDate: moment(),
      pastDate: moment().subtract(60, 'days'),
      label: intl.formatMessage({
        id: `${sfPrefix}-60days`,
        defaultMessage: '60 days',
      })
    },
    {
      id: 'last90Days',
      timePeriod: 90,
      todayDate: moment(),
      pastDate: moment().subtract(90, 'days'),
      label: intl.formatMessage({
        id: `${sfPrefix}-90days`,
        defaultMessage: '90 days',
      })
    },
  ];

  const smartFilterByTimePeriod = (setFieldValue, item, handleSubmit) => {
    setFieldValue('timePeriod', `${item.timePeriod}`);
    setFieldValue('fromDate', item.pastDate);
    setFieldValue('toDate', item.todayDate);
    handleSubmit();
  };

  const exportReceiptsFileFunc = () => {
    setLoading(true);
    dispatch(actions.exportReceiptsFileAction(receiptsUIProps.queryParams))
      .then(prepareDataForExcel)
      .then((data) => {
        generateExcelFile(data, 'Receipts.xlsx', receiptsHeader);
      })
      .catch((error) => {
        console.error(`Error: + ${error}`);
        setLoading(false);
      })
      .finally(() => setLoading(false));
  };
  const prepareDataForExcel = data => {
    return data.map((item, index) => {
      const {
        _id,
        agentId,
        agentName,
        customerID,
        customerName,
        date,
        sum,
        invoices,
        invoiceCreatedAt
      } = item;

      const invoicesList = Boolean(invoices) && invoices.length > 0
        ? invoices.join(' ,')
        : '';

      return {
        [headersReceipts[0]]: index + 1,
        [headersReceipts[1]]: _id,
        [headersReceipts[2]]: agentId,
        [headersReceipts[3]]: agentName,
        [headersReceipts[4]]: customerID,
        [headersReceipts[5]]: customerName,
        [headersReceipts[6]]: Boolean(date)
          ? moment(date).format('DD.MM.YYYY') : '',
        [headersReceipts[7]]: sum ? parseFloat(sum.toFixed(2)) : '',
        [headersReceipts[8]]: invoicesList,
        [headersReceipts[9]]: Boolean(invoiceCreatedAt)
          ? moment(invoiceCreatedAt).format('DD.MM.YYYY') : '',
      };
    });
  };

  const containerClassnames = 'w-200px position-relative my-1 px-3';

  return (
    <Formik
      initialValues={{
        agentID,
        agentName,
        customerID,
        invoiceStatusID,
        fromDate,
        toDate,
        receiptID,
        amount,
        timePeriod: timePeriodValue || '0',
      }}
      onSubmit={(values) => applyFilter(values)}
      enableReinitialize={true}
    >
      {({values, ...formikProps}) => (
        <FiltersBar
          filters={
            <div className={'d-flex flex-wrap justify-content-center align-items-center mb-0 w-100'}>
              <FilterTextField
                fieldName="agentID"
                placeholder={intl.formatMessage({id: `${prefix}-agentID`, defaultMessage: 'Agent Id'})}
                value={values.agentID}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterTextField
                fieldName="agentName"
                placeholder={intl.formatMessage({id: `${prefix}-agentName`, defaultMessage: 'Agent name'})}
                value={values.agentName}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterTextField
                fieldName="customerID"
                placeholder={intl.formatMessage({id: `${prefix}-customerID`, defaultMessage: 'Customer Id'})}
                value={values.customerID}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterFromDate
                values={values}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterToDate
                values={values}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterTextField
                fieldName="receiptID"
                placeholder={intl.formatMessage({id: `${prefix}-receiptID`, defaultMessage: 'Receipt Id'})}
                value={values.receiptID}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterTextField
                fieldName="amount"
                placeholder={intl.formatMessage({id: `${prefix}-amount`, defaultMessage: 'Amount'})}
                value={values.amount}
                containerStyles={containerClassnames}
                {...formikProps}
              />
            </div>
          }
          buttons={
            <div className="d-flex align-items-center justify-content-between">
              <ButtonSmartFilter
                header={intl.formatMessage({id: `${sfPrefix}-header`, defaultMessage: 'Smart filter'})}
              >
                <Dropdown.Item as={CustomDDItem}>
                  <div className="d-flex w-100">
                    <h7 className={styles.filterName}>
                      {intl.formatMessage({
                        id: `${sfPrefix}-time-period-header`,
                        defaultMessage: 'Invoice time period'
                      })}
                    </h7>
                    <div className="d-flex flex-wrap">
                      <div className="d-flex flex-wrap">
                        {smartFilterDaysArray.map((item, index) => {
                          return (
                            <div key={index} className="invoices-smart-filter-time-item">
                              <input
                                type="radio"
                                className="hide-checkbox"
                                id={item.id}
                                name="timePeriod"
                                onBlur={formikProps.handleBlur}
                                checked={values.timePeriod === item.timePeriod}
                                onChange={() => {
                                  smartFilterByTimePeriod(formikProps.setFieldValue, item, formikProps.handleSubmit);
                                }}
                              />
                              <label htmlFor={item.id} className="custom-checkbox mr-4">
                                {item.label}
                              </label>
                            </div>
                          );
                        })
                        }
                      </div>
                    </div>
                  </div>
                </Dropdown.Item>
              </ButtonSmartFilter>
              <ButtonExportToExcel
                loading={loading}
                exportFunc={exportReceiptsFileFunc}
              />
              <PopupAddReceipt setFieldValue={formikProps.setFieldValue}/>
            </div>
          }
        />
      )}
    </Formik>
  );
}